import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import 'src/content/screens/d-slide-divider';
import 'src/content/screens/d-slide-media';
import 'src/content/screens/d-edit-html-for-slide';
import 'src/content/screens/d-view-html-for-slide';
import type { SlideDivider, SlideMedia, SlideText, CustomSlide, SlideElement } from 'src/content/screens/slide-element';
import type { LibraryItem } from 'src/library/screen/library-item';
import { plainTextFromHtml } from 'src/utilities/text';

/**
 *
 */
@customElement('d-custom-slide')
export class DCustomSlide extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
    :host > * {
      box-sizing: border-box;
    }
    .slide {
      container-type: inline-size;
      position: relative;
      box-sizing: border-box;
      aspect-ratio: 16/9;
      background: white;
      overflow: hidden;
    }
    d-edit-division d-edit-division {
      container-type: normal;
    }
    #tiptap-menu {
      background: white;
      padding: 12px;
      border-radius: 12px;
      visibility: hidden;
      opacity: 0;
      position: absolute;
      filter: drop-shadow(0 2px 10px hsla(1, 0%, 0%, 0.3));
      transition: all 0.3s;
      line-height: 0;
      z-index: 99999;
    }
    #tiptap-menu.is-active {
      display: flex;
    }
    #tiptap-menu button {
      -moz-appearance: none;
      -webkit-appearance: none;
      height: 30px;
      width: 30px;
      font-size: 15px;
      text-align: center;
      cursor: pointer;
      outline: none;
      border: none;
      border-radius: 4px;
      background-color: hsl(0, 0%, 92%);
      background-image: url(/images/formatbuttons.svg);
      background-repeat: no-repeat;
      background-size: 310px 70px;
    }
    #tiptap-menu button.bold {
      background-position: 0 0;
    }
    #tiptap-menu button.italic {
      background-position: -40px 0;
    }
    #tiptap-menu button.bullets {
      background-position: -80px 0;
    }
    #tiptap-menu button.numbers {
      background-position: -120px 0;
    }
    #tiptap-menu button.active {
      background-color: #696969;
      background-position-y: -40px;
    }
    #tiptap-menu button.align {
      background-image: url(/images/formatbuttons-align.svg);
      background-size: 90px 60px;
      background-position: 0 0;
    }
    #tiptap-menu button.align.center {
      background-image: url(/images/formatbuttons-align.svg);
      background-position-x: -30px;
    }
    #tiptap-menu button.align.right {
      background-image: url(/images/formatbuttons-align.svg);
      background-position-x: -60px;
    }
    #tiptap-menu button.align.active {
      background-position-y: -30px;
    }
  `;
  @property({ type: Object })
  slide: CustomSlide | undefined = undefined;
  @property({ type: Boolean })
  edit = false;
  @property({ type: Boolean })
  thumbnail = false;
  @property({ type: Array })
  media: string[] = [];
  @property({ type: Array })
  library: LibraryItem[] = [];
  @property({ type: String })
  assetBase = '';
  @property({ type: Boolean })
  hovered = false;
  @state() private activeEditorId: string = '';
  /**
   * To prevent unwanted text selection, d-edit-html-for-slide
   * is replaced by d-view-html-for-slide while divider is moving #12812
   */
  @state() private dividerActive: boolean = false;

  private fireChange() {
    // Thumbnail-modus brukes for forhåndsvisning av layout-maler og suggestions —
    // ikke en redigerbar instans. Når media inni en thumbnail laster ferdig
    // kjører d-slide-media.fitImageCentered → fireValuesChanged, som vi her
    // fanger via mediaChanged. Lar vi value-changed boble videre, fanges den
    // av d-edit-slide-dialog (som ikke filtrerer på opprinnelse) og parent-
    // sliden byttes til thumbnailens innhold. Stopp propagering når vi ikke
    // er den faktiske editor-instansen.
    if (!this.slide || !this.edit) return;
    this.dispatchEvent(
      new CustomEvent<{ value: CustomSlide }>('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.slide },
      }),
    );
  }

  updateElement(
    element: SlideElement,
    id: string,
    value: number | string | { left: number; top: number; scale: number },
  ) {
    if (id === element.id) {
      if (element.type === 'media' && typeof value === 'object') {
        element.left = value.left;
        element.top = value.top;
        element.scale = value.scale;
      }
    }
    if (element.type === 'divider') {
      if (id === element.id && typeof value === 'number') {
        element.firstDivisionSize = value;
        return element;
      } else {
        if (element.first) {
          element.first = this.updateElement(element.first, id, value);
        }
        if (element.last) {
          element.last = this.updateElement(element.last, id, value);
        }
      }
    }
    if (element.overlayElement) {
      element.overlayElement = this.updateElement(element.overlayElement, id, value);
    }
    return element;
  }

  private updateSlide(
    id: string,
    type: 'divider' | 'header' | 'text' | 'media',
    value: number | string | { src: string; left: number; top: number; scale: number; videoDuration: number },
  ) {
    if (this.slide) {
      const slide: CustomSlide = { ...this.slide };
      if ((type === 'header' || type === 'text') && typeof value === 'string') {
        slide[type] = value;
        if (type === 'header') {
          // Bruk header-tekst (uten markup) som slide-navn så thumbnail-titlen
          // i playlist-editoren oppdateres automatisk når brukeren skriver.
          slide.name = plainTextFromHtml(value);
        }
      }
      if (type === 'media' && typeof value === 'object') {
        slide.media = value.src;
        slide.duration = value.videoDuration ?? 10;
      }
      slide.layout = this.updateElement(slide.layout, id, value);
      this.slide = slide;
      this.fireChange();
    }
  }

  private onDividerMoveStart() {
    this.dividerActive = true;
  }

  private onDividerMoveStop() {
    this.dividerActive = false;
  }

  private dividerChanged(e: CustomEvent<{ value: number }>, id: string) {
    e.stopPropagation();
    this.dividerActive = false;
    this.updateSlide(id, 'divider', e.detail.value);
  }

  private htmlChanged(e: CustomEvent<{ value: string }>, type: 'header' | 'text', id: string) {
    e.stopPropagation();
    this.updateSlide(id, type, e.detail.value);
  }

  private mediaChanged(
    e: CustomEvent<{ src: string; left: number; top: number; scale: number; videoDuration: number }>,
    id: string,
  ) {
    e.stopPropagation();
    this.updateSlide(id, 'media', e.detail);
  }

  private layoutPosition(
    mainOrientation: 'horizontal' | 'vertical' | '',
    slot: 'first' | 'last' | '',
  ): 'full' | 'top' | 'right' | 'bottom' | 'left' {
    if (mainOrientation === 'horizontal') {
      if (slot === 'first') {
        return 'left';
      }
      if (slot === 'last') {
        return 'right';
      }
    }
    if (mainOrientation === 'vertical') {
      if (slot === 'first') {
        return 'top';
      }
      if (slot === 'last') {
        return 'bottom';
      }
    }
    return 'full';
  }

  private renderDivision(
    item: SlideDivider,
    slot: 'first' | 'last' | '',
    mainOrientation: 'horizontal' | 'vertical' | '',
  ) {
    let subDivider = true;
    if (!mainOrientation) {
      subDivider = false;
      mainOrientation = item.orientation;
    }
    return html`
      <d-slide-divider
        slot="${slot}"
        .edit=${this.edit}
        .vertical=${item.orientation === 'vertical'}
        .subDivider=${subDivider}
        ?hide-controls="${!this.hovered}"
        .value=${item.firstDivisionSize ?? 0}
        @divider-move-start=${() => this.onDividerMoveStart()}
        @divider-move-stop=${() => this.onDividerMoveStop()}
        @value-changed=${(e: CustomEvent<{ value: number }>) => this.dividerChanged(e, item.id ?? '')}
      >
        ${this.renderSlideContent(item.first, 'first', slot, mainOrientation)}
        ${this.renderSlideContent(item.last, 'last', slot, mainOrientation)}
      </d-slide-divider>
    `;
  }

  private renderText(
    item: SlideText,
    slot: 'first' | 'last' | '',
    layoutPosition: 'full' | 'top' | 'right' | 'bottom' | 'left',
  ) {
    if (this.slide) {
      let content = this.slide.text;
      if (item.type === 'header') {
        content = this.slide.header;
      }
      if (this.edit && !this.dividerActive) {
        return html`
          <d-edit-html-for-slide
            slot="${slot}"
            .activeId=${this.activeEditorId}
            .header=${item.type === 'header'}
            .layoutPosition=${layoutPosition}
            .textColor=${item.textColor ?? 'black'}
            .textAlign=${item.textAlign ?? 'left'}
            .value=${content}
            @editor-activated=${(e: CustomEvent) => (this.activeEditorId = e.detail.editorId)}
            @value-changed=${(e: CustomEvent<{ value: string }>) => this.htmlChanged(e, item.type, item.id ?? '')}
          ></d-edit-html-for-slide>
        `;
      }
      return html` <d-view-html-for-slide
        slot="${slot}"
        .wireframe=${this.thumbnail}
        .header=${item.type === 'header'}
        .layoutPosition=${layoutPosition}
        .textColor=${item.textColor ?? 'black'}
        .textAlign=${item.textAlign ?? 'left'}
        value=${content}
      ></d-view-html-for-slide>`;
    }
    return nothing;
  }

  private renderMedia(
    item: SlideMedia,
    slot: 'first' | 'last' | '',
    layoutPosition: 'full' | 'top' | 'right' | 'bottom' | 'left',
  ) {
    return html`
      <d-slide-media
        slot="${slot}"
        .edit=${this.edit}
        ?hide-controls="${!this.hovered}"
        .layoutPosition=${layoutPosition}
        .src=${this.slide?.media ?? ''}
        .left=${item.left}
        .top=${item.top}
        .scale=${item.scale}
        .overlayColor=${item.overlayColor ?? ''}
        .blurring=${item.blur ?? 0}
        .media=${this.media}
        .library=${this.library}
        .assetBase=${this.assetBase}
        @values-changed=${(
          e: CustomEvent<{ src: string; left: number; top: number; scale: number; videoDuration: number }>,
        ) => this.mediaChanged(e, item.id ?? '')}
      >
        ${item.overlayElement
          ? html` ${this.renderSlideContent(item.overlayElement, '', slot, 'horizontal')} `
          : nothing}
      </d-slide-media>
    `;
  }

  private renderSlideContent(
    item: SlideElement | undefined,
    slot: 'first' | 'last' | '',
    parentSlot: 'first' | 'last' | '',
    mainOrientation: 'horizontal' | 'vertical' | '',
  ) {
    if (!item || !item.type) return nothing;
    if (item.type === 'divider') {
      return this.renderDivision(item, slot, mainOrientation);
    }
    if (item.type === 'header' || item.type === 'text') {
      return this.renderText(item, slot, this.layoutPosition(mainOrientation, parentSlot));
    }
    if (item.type === 'media') {
      return this.renderMedia(item, slot, this.layoutPosition(mainOrientation, slot));
    }
    return nothing;
  }

  /**
   * Fallback når layout-treet mangler eller er ugyldig — typisk for lagrede
   * slides som persisterer hele payloaden men der layout-tre-en av en eller
   * annen grunn ikke kommer tilbake intakt fra serveren. Vi rendrer header
   * og text i en enkel vertikal stack så thumbnail-en i hvert fall viser
   * innholdet og brukeren kan kjenne sliden igjen.
   */
  private renderLayoutFallback() {
    const wrap = document.createElement('div');
    wrap.style.cssText =
      'box-sizing:border-box;width:100%;height:100%;padding:5cqi 7cqi;display:flex;flex-direction:column;justify-content:center;gap:3cqi;background:white;color:black;';
    if (this.slide?.header) {
      const header = document.createElement('div');
      header.style.cssText = "font-family:'Source Sans 3',sans-serif;font-weight:600;font-size:6cqi;line-height:1.06;";
      header.innerHTML = this.slide.header;
      wrap.appendChild(header);
    }
    if (this.slide?.text) {
      const text = document.createElement('div');
      text.style.cssText = "font-family:'Source Serif 4',serif;font-weight:400;font-size:3.5cqi;line-height:1.3;";
      text.innerHTML = this.slide.text;
      wrap.appendChild(text);
    }
    return wrap;
  }

  render() {
    if (!this.slide) return nothing;
    const layoutValid = this.slide.layout && this.slide.layout.type;
    return html`
      <div class="slide" @mouseover=${() => (this.hovered = true)} @mouseout=${() => (this.hovered = false)}>
        ${layoutValid ? this.renderSlideContent(this.slide.layout, '', '', '') : this.renderLayoutFallback()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-custom-slide': DCustomSlide;
  }
}
